<template>
  <cupertino-alert-dialog :title="title" :close="onClickOutSide">
    <template v-slot:content>
      <div class="px-1 py-1">
        <input class="box-border w-full px-1/2 py-1/2" v-model="value" />
      </div>
    </template>
    <template v-slot:actions>
      <div class="flex flex-row flex-center flex-spacearound">
        <div class="w-full flex flex-center pointer py-1 hover left-bottom-radius-10 font-bold" @click="onClickClose">
          <span>{{ close }}</span>
        </div>
        <div class="w-full flex flex-center pointer py-1 hover right-bottom-radius-10 font-bold" @click="onClickSubmit">
          <span class="color-blue">{{ submit }}</span>
        </div>
      </div>
    </template>
  </cupertino-alert-dialog>
</template>

<script lang="ts">
import Vue from "vue";
import CupertinoAlertDialog from "@/components/ui/dialogs/CupertinoAlertDialog.vue";

interface Props {
  title: string;
  close?: string;
  submit?: string;
  submitHandler?: (value: string) => void;
}

interface Data {
  value: string;
}

interface Computed {}

interface Methods {
  onClickOutSide: () => void;
  onClickClose: () => void;
  onClickSubmit: () => void;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { CupertinoAlertDialog },

  props: {
    title: String,
    close: {
      type: String,
      default: "閉じる",
      required: false,
    },
    submit: {
      type: String,
      default: "送信",
      required: false,
    },
    submitHandler: {
      type: Function,
      required: false,
    },
  },

  data() {
    return {
      value: "",
    };
  },

  methods: {
    onClickOutSide() {
      this.$close(false);
    },
    onClickClose() {
      this.$close(false);
    },
    onClickSubmit() {
      if (this.submitHandler) this.submitHandler(this.value);
      this.$close(true);
    },
  },
});

export type InputDialogProps = Props;
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

// container

.w-full {
  width: 100%;
}

.box-border {
  box-sizing: border-box;
}

// button

.pointer {
  cursor: pointer;
}

.hover {
  position: relative;
  @include hover-overlay;
}

// input

input {
  background-color: var(--color-surface-subtle);
  color: var(--color-text-normal);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xs);

  &::placeholder {
    color: var(--color-text-low);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

// flex

.flex {
  display: flex;
}

.flex-row {
  flex-direction: row;
}

.flex-center {
  align-items: center;
  justify-content: center;
}

.flex-spacearound {
  justify-content: space-around;
}

// padding, margin

.px-1\/2 {
  padding-right: 0.5rem;
  padding-left: 0.5rem;
}

.py-1\/2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.px-1 {
  padding-right: 1em;
  padding-left: 1em;
}

.py-1 {
  padding-top: 1em;
  padding-bottom: 1em;
}

.mx-1 {
  margin-right: 1rem;
  margin-left: 1rem;
}

.my-1 {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

// border

.bottom-radius-10 {
  border-radius: 0px 0px var(--radius-sm) var(--radius-sm);
}

.left-bottom-radius-10 {
  border-radius: 0px 0px 0px var(--radius-sm);
}

.right-bottom-radius-10 {
  border-radius: 0px 0px var(--radius-sm) 0px;
}

.font-bold {
  font-weight: var(--font-weight-bold);
}

// color

.color-blue {
  color: var(--color-primary);
}

// text

.text-center {
  text-align: center;
}
</style>
